import type { EnvioDaConversa, EstadoSimulacao, SimulacaoComposerStatus } from '../types';
import { desfechoDesconhecido } from './intencaoDeComando';
import { ROTULO_DO_ENVIO } from './rotulosDaSimulacao';

/** Teto do POST /mensagens (CA-9). */
export const LIMITE_DO_TEXTO = 4_096;

/**
 * "Tentar novamente" só aparece quando repetir pode dar outro desfecho: rede, 5xx, rodada em curso
 * ou comando que o core marcou FALHOU. Recusa 4xx sem comando (opção fora do estado, texto
 * inválido) daria a mesma recusa.
 */
export const podeTentarDeNovo = (envio: EnvioDaConversa): boolean =>
  Boolean(envio.comandoId) ||
  desfechoDesconhecido(envio.erro) ||
  envio.erro?.codigo === 'RODADA_EM_CURSO';

/**
 * O campo depois de um envio que falhou. O enviado volta quando o campo está vazio. Se a pessoa
 * já digitou outra coisa, fica o que ela digitou, desde que o "Tentar novamente" reenvie o
 * original. Sem nova tentativa, o original só existiria no store: ele volta na frente do texto
 * novo, para não sumir da tela. Campo só com espaços conta como vazio, como no envio. A junção
 * pode passar de `LIMITE_DO_TEXTO`, e o `enviar` não sai (`textoValidoParaEnvio`) até a pessoa
 * encurtar o texto — caso raro, aceito para não descartar nenhum dos dois textos.
 *
 * @example rascunhoAposFalha('', 'Oi', true) // 'Oi'
 */
export const rascunhoAposFalha = (
  noCampo: string,
  enviado: string,
  podeRepetir: boolean,
): string => {
  if (!noCampo.trim()) return enviado;
  return podeRepetir ? noCampo : `${enviado}\n${noCampo}`;
};

/**
 * Os estados do compositor do CA-9: pendente (requisição em voo), processando (o core ainda roda o
 * flow), entregue, falhou e encerrado. Antes do primeiro envio, nada aparece.
 */
export const statusDoCompositor = (
  simulacao: EstadoSimulacao | null,
  envio: EnvioDaConversa | null,
): SimulacaoComposerStatus | null => {
  if (!simulacao) return null;
  if (simulacao.status === 'ENCERRADA') {
    return { text: ROTULO_DO_ENVIO.encerrado, tone: 'neutral', canRetry: false };
  }
  if (!envio) return null;
  if (envio.status === 'enviando') {
    return { text: ROTULO_DO_ENVIO.pendente, tone: 'neutral', canRetry: false };
  }
  if (envio.status === 'falhou') {
    return {
      text: envio.erro?.mensagem ?? 'Não foi possível enviar. Tente novamente.',
      tone: 'error',
      canRetry: podeTentarDeNovo(envio),
    };
  }
  const text = simulacao.processando ? ROTULO_DO_ENVIO.processando : ROTULO_DO_ENVIO.entregue;
  return { text, tone: 'neutral', canRetry: false };
};

export const textoValidoParaEnvio = (texto: string): boolean => {
  const conteudo = texto.trim();
  return conteudo.length > 0 && conteudo.length <= LIMITE_DO_TEXTO;
};
